import { localize } from '@/base/locale/lang';
import vscode from '@/base/platform/vscode';
import { baseUrl } from "@/base/rpc/rpc-config";
import rpcService, { withLocalePathSegment } from "@/base/rpc/rpc-service";
import state from "@/base/state";
import { Empty, Layout, Row, Space } from "antd";
import { Content, Footer, Header } from "antd/lib/layout/layout";
import * as React from "react";
import { Scrollbars } from 'react-custom-scrollbars';
import ReactMarkdown from 'react-markdown';
import { Link, Params, useNavigate, useParams } from "react-router-dom";
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { a11yDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import remarkToc from 'remark-toc';
import { HOME_PAGE } from "../configs";
import { fetchChildren } from "../services/doc-service";
import '../styles/doc.css';
import { DocumentNodeType, IDocument, IDocumentOutline, IDocumentResponse } from "../types/doc-types";
import { getHeadingElementId } from "../utils";
import { getEditionFromParams, getEditionInfoFromParams } from "../utils/edition";
import { getBasePathFromParam } from "../utils/urls";

const getImageUrl = (doc: IDocument, imgSrc: string | undefined, urlParams: Readonly<Params<string>>): string => {
    const edition = getEditionFromParams(urlParams);
    return `${baseUrl}${baseUrl.endsWith('/') ? '' : '/'}${edition}/${withLocalePathSegment(edition, state.locale)}get-image?doc-path=${encodeURIComponent(doc.Path)}&doc-key=${
        doc.Key
    }&image=${encodeURIComponent(imgSrc || '')}`;
}

const getHyperRef = (doc: IDocument, originHref?: string): string => {
    let href = '';
    if (originHref?.endsWith('/index.md')) {
        href = originHref.replace(/\/index\.md$/, '');
    } else if (originHref?.endsWith('.md')) {
        href = originHref.replace(/\.md$/, '');
    }
    href = href?.replace(/(\/|^)(\d+-)/g, '$1');
    if (doc.NodeType === DocumentNodeType.FolderWithContent) {
        
    }
    return '';
}

const isEmptyContainerDoc = (doc: IDocument) => {
    if (doc.NodeType === DocumentNodeType.Folder || ! doc.Content) {
        return true;
    }
    return doc.Content.length < 100 && /^#\s+[^\n\r]+\s*$/.test(doc.Content);
}

const getDocument = async (idOrKey: number | string, edition: string) => {
    const res = await rpcService.getJSON<IDocumentResponse>(`/${edition}/${withLocalePathSegment(edition, state.locale)}doc`, typeof idOrKey === 'number' ? {id: idOrKey} : {key: idOrKey});
    return res.data;
}

const DocContent = (props: {doc?: IDocument}) => {
    const params = TARGET === 'web' ? useParams(): {};
    const navigate = TARGET === 'web' ? useNavigate() : (to: string, options: any) => {};

    const [doc, setDoc] = React.useState<IDocument | undefined>(props.doc);

    const [subNodes, setSubNodes] = React.useState<IDocumentOutline[]>();

    const scrollBar = React.useRef<Scrollbars>(null);

    React.useEffect(() => {
        setDoc(undefined);
        if (TARGET === 'vscode') {
            window.onmessage = function(e) {
                const doc = e.data as IDocument;
                if (doc && typeof doc === 'object' && doc.Key && doc.Title) {
                    setDoc(doc);
                }
            }
            vscode.postMessage({
                type: 'RenderOK',
            });
        } else if (TARGET === 'web') {
            const id = parseInt(params.id || '0');
            const key = params['*'] || '';
            if (key.endsWith('/')) {
                navigate(`${getBasePathFromParam(params)}/d/${key.substring(0, key.length - 1)}`, {replace: true});
            }
            if (id || key) {
                (async() => {
                    const edition = getEditionInfoFromParams(params);
                    const doc = await getDocument(id || key, edition.key)
                    setDoc(doc);
                    if (isEmptyContainerDoc(doc)) {
                        fetchAndSetSubNodes(doc);
                    }
                    document.title = localize('pageTitle', {
                        docTitle: doc.Title,
                        editionTitle: edition.title,
                    });
                })();
            } else {
                navigate(`${getBasePathFromParam(params)}/d/${HOME_PAGE}`, {replace: true});
            }
        }
        scrollBar.current?.scrollToTop();
    }, [params.id, params['*']]);

    const renderSubNodesIndex = (subNodes: IDocumentOutline[], style?: React.CSSProperties) => 
        <ul>
        {subNodes.map(sn => ( 
            <li key={sn.Key} style={{marginBottom: '1em', ...style}}>
                <Link to={`${getBasePathFromParam(params)}/d/${sn.Key}`} key={sn.Key}>{sn.Title}</Link>
            </li>
        ))}
        </ul>;

    const fetchAndSetSubNodes = (doc: IDocument) => {
        fetchChildren({edition: getEditionFromParams(params), key: doc.Key}).then(res => {
            setSubNodes(res.data);
        });
    }
    

    const ids: Record<string, number> = {};

    return doc ?
        <Layout  style={{background: '#FFF', height: '100%', padding: '1em'}}>
            <Header
                className="doc-header"
                style={{
                    margin: '-1em',
                    padding: '1em',
                    paddingBottom: 0,
                    marginBottom: '1em',
                    height: 'auto',
                    background: '#FFF',
                    borderBottom: '#EEE 1px solid',
                }}>
                    <h1>{doc.Title}</h1>
                    {doc.Author && <Row style={{lineHeight: '32px', color: '#AAA', paddingLeft: '2px',}}>
                        <Space>
                        {/* <Col>最近更新:</Col>
                        <Col>{doc.Author}</Col>
                        <Col>{dayjs(doc.CommitTime).format('YYYY-MM-DD HH:mm:ss')}</Col> */}
                        </Space>
                    </Row>}
            </Header>
            <Content
                style={{
                    height: 'calc(100% - 1em)',
                    marginTop: '1em',
                }}
            >
                <Scrollbars autoHide ref={scrollBar}>
                <ReactMarkdown
                    className="doc-core-content"
                    // skipHtml
                    remarkPlugins={[remarkToc, remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    children={doc.Content}
                    components={{
                    // @ts-ignore
                    subdocs({node, children, ...props}) {
                        if (subNodes) {
                            return renderSubNodesIndex(subNodes, props?.style);
                        } else if (doc.NodeType !== DocumentNodeType.PureContent) {
                            fetchAndSetSubNodes(doc);
                        } else {
                            return <></>;
                        }
                    },
                    blockquote({node, className, children, ...props}) {
                        return <blockquote style={{backgroundColor: '#F5F5F5', borderLeft: '#CCC 5px solid', padding: '1px 5px 0', fontSize: '0.9em'}}>{children}</blockquote> 
                    },
                    img({node, children, ...props}) {
                        props.src = getImageUrl(doc, props.src, params);
                        return <img {...props}></img>;
                    },
                    a({node, children, ...props}) {
                        let {href} = props;
                        if (href?.endsWith('/index.md')) {
                            href = href.replace(/\/index\.md$/, '');
                        } else if (href?.endsWith('.md')) {
                            href = href.replace(/\.md$/, '');
                        }
                        href = href?.replace(/(\/|^)(\d+-)/g, '$1');
                        if (href && ! (href.startsWith('#') || href.startsWith('http://') || href.startsWith('https://'))) {
                            return <Link to={new URL(href, location.href)}>{children}</Link>;
                        }
                        return <a href={href}>{children}</a>
                    },
                    h2({node, className, children, ...props}) {
                        return <h2 id={getHeadingElementId(node, ids)}>{children}</h2>
                    },
                    h3({node, className, children, ...props}) {
                        return <h3 id={getHeadingElementId(node, ids)}>{children}</h3>
                    },
                    h4({node, className, children, ...props}) {
                        return <h4 id={getHeadingElementId(node, ids)}>{children}</h4>
                    },
                    h5({node, className, children, ...props}) {
                        return <h5 id={getHeadingElementId(node, ids)}>{children}</h5>
                    },
                    h6({node, className, children, ...props}) {
                        return <h6 id={getHeadingElementId(node, ids)}>{children}</h6>
                    },
                    code({node, inline, className, children, ...props}) {
                        const match = /language-(\w+)/.exec(className || 'language-unknown')
                        return !inline && match ? (
                            <SyntaxHighlighter
                                children={String(children).replace(/\n$/, '')}
                                // @ts-ignore
                                style={a11yDark}
                                language={match[1]}
                                PreTag='div'
                                {...props}
                            />
                        ) : (
                            <code
                                className={className}
                                {...props}
                                style={{
                                    backgroundColor: '#F2F5F8',
                                    borderRadius: '0.2em',
                                    padding: '0.1em 0.3em',
                                }}
                            >
                                {children}
                            </code>
                        );
                    }
                    }}
                />
                {isEmptyContainerDoc(doc) && subNodes && renderSubNodesIndex(subNodes)}
            </Scrollbars>
            </Content>
            <Footer style={{height: '0.5em', marginTop: '0.5em', background: 'transparent', borderTop: '#EEE 1px solid'}}>
            </Footer>
        </Layout>:
        <Empty />;
}

export default DocContent;
